// IC-008 — pure logic for the "Microsoft Entra ID" preset in SignInCard.jsx's add/edit provider
// form. No DOM/browser APIs, kept unit-testable in isolation (mirrors lib/authErrors.js).
//
// Server-side validation is the real gate (apps/api/src/services/sso-providers.ts's
// validateIssuer() just requires https, it doesn't know about Entra specifically) — this module
// only builds the issuer/discoveryEndpoint the Entra preset would submit, and gives the customer
// an immediate, specific error before they ever hit "Save", for the one mistake Entra setup guides
// commonly lead people into: pasting the multi-tenant endpoint alias ("common"/"organizations")
// instead of their actual Directory (tenant) ID. A provider registered against "common" would
// still pass the API's own `issuer` check (it's a valid https URL) but would accept sign-ins from
// any Microsoft tenant, not just the customer's own — a real security footgun this preset exists
// to prevent, not just a UX nicety.

export const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Microsoft's own reserved multi-tenant endpoint aliases (see Entra's "common"/"organizations"/
// "consumers" endpoints). "common"/"organizations" are the two the brief calls out explicitly —
// both accept sign-ins from arbitrary external tenants, which is never what a single organisation's
// SSO provider registration should point at.
const RESERVED_TENANT_ALIASES = new Set(['common', 'organizations']);

/**
 * @param {string | null | undefined} raw
 * @returns {{ valid: true, tenantId: string } | { valid: false, error: string }}
 */
export function validateEntraTenantId(raw) {
  const tenant = (raw ?? '').trim();
  if (!tenant) return { valid: false, error: 'Directory (tenant) ID is required' };
  if (RESERVED_TENANT_ALIASES.has(tenant.toLowerCase())) {
    return {
      valid: false,
      error: `"${tenant}" is a multi-tenant Microsoft endpoint alias, not a Directory (tenant) ID — use your organisation's actual tenant ID (a GUID), not "common" or "organizations"`,
    };
  }
  if (!GUID_PATTERN.test(tenant)) {
    return { valid: false, error: 'Directory (tenant) ID must be a GUID, e.g. 3fa85f64-5717-4562-b3fc-2c963f66afa6 — find it on the Entra app registration\'s Overview page' };
  }
  return { valid: true, tenantId: tenant.toLowerCase() };
}

/** @param {string} tenantId - already-validated tenant GUID */
export function entraIssuerFor(tenantId) {
  return `https://login.microsoftonline.com/${tenantId}/v2.0`;
}

/** @param {string} tenantId - already-validated tenant GUID */
export function entraDiscoveryFor(tenantId) {
  return `${entraIssuerFor(tenantId)}/.well-known/openid-configuration`;
}

/**
 * Validates a raw tenant ID and, if valid, derives the issuer + discovery endpoint the "Microsoft
 * Entra ID" preset fills in (read-only, with an edit toggle, per the brief).
 * @param {string | null | undefined} rawTenantId
 * @returns {{ valid: true, tenantId: string, issuer: string, discoveryEndpoint: string } | { valid: false, error: string }}
 */
export function buildEntraPreset(rawTenantId) {
  const result = validateEntraTenantId(rawTenantId);
  if (!result.valid) return result;
  return {
    valid: true,
    tenantId: result.tenantId,
    issuer: entraIssuerFor(result.tenantId),
    discoveryEndpoint: entraDiscoveryFor(result.tenantId),
  };
}
